import { parseISO, subDays } from 'date-fns';
import logger from '../../logging';
import {
  CoverageTierToCoverageTierV2,
  EditMemberGroup,
  IdentificationCodeType,
  Plan,
  RelationshipToRelationshipV2,
} from '../../models';
import { formatISODate } from '../../util';
import { createMemberGroup, updateMemberGroup } from './api';

const log = logger(__filename);

function getApiMemberGroup(memberGroup: EditMemberGroup, plans: Plan[]) {
  const primaryAddress = {
    address_line_1: memberGroup.primary.addressLine1,
    address_line_2: memberGroup.primary.addressLine2 ?? null,
    city: memberGroup.primary.city,
    state: memberGroup.primary.state,
    zip_code: memberGroup.primary.zipCode,
  };

  const plan = plans.find(p => p.id === memberGroup.benefitPlanId);

  if (!plan?.endDate) {
    log.error({ benefitPlanId: memberGroup.benefitPlanId }, 'could not find end date for plan');
    throw new Error('could not find end date for plan');
  }
  const planEndDate = plan.endDate;

  const members = [memberGroup.primary]
    .concat(memberGroup.dependents.filter(member => !member.isPastTerminated))
    .map(member => {
      let benefitEndDate = member.benefitEndDate ?? planEndDate.split('T')[0];
      if (member.isCancelTerminated) {
        benefitEndDate = formatISODate(subDays(parseISO(member.benefitStartDate), 1));
      }
      return {
        first_name: member.firstName,
        last_name: member.lastName,
        identification_code: member.ssn ?? null,
        identification_code_type: member.identificationCodeType ?? IdentificationCodeType.SSN,
        benefit_start_date: member.benefitStartDate,
        benefit_end_date: benefitEndDate,
        date_of_birth: member.birthDate ?? null,
        gender: member.gender ?? null,
        address_line_1: member.addressLine1 ?? null,
        address_line_2: member.addressLine2 ?? null,
        city: member.city ?? null,
        state: member.state ?? null,
        zip_code: member.zipCode ?? null,
        email: member.email ?? null,
        phone: member.phone ?? null,
        funding_source: member.fundingSource ?? null,
        garner_card_issued_at: member.garnerCardIssuedAt ?? null,
        relationship_code: RelationshipToRelationshipV2[member.relationshipCode],
        employee_id: memberGroup.primary.externalEmployeeId ?? memberGroup.primary.ssn,
        ...(member.isAddressSameAsPrimary ? primaryAddress : undefined),
      };
    });

  return {
    coverage_tier: CoverageTierToCoverageTierV2[memberGroup.coverageTier],
    plan_id: memberGroup.benefitPlanId,
    client_id: memberGroup.clientId,
    client_organization_id: memberGroup.clientOrganizationId ? parseInt(memberGroup.clientOrganizationId) : null,
    sub_organization_id: memberGroup.subOrganizationId ?? null,
    invoice_group: memberGroup.invoiceGroup ?? null,
    reporting_group: memberGroup.reportingGroup ?? null,
    members: members,
  };
}

export function saveNewMemberGroup(memberGroup: EditMemberGroup, plans: Plan[]) {
  const apiMemberGroup = getApiMemberGroup(memberGroup, plans);
  return createMemberGroup(apiMemberGroup);
}

export function saveExistingMemberGroup(memberGroup: EditMemberGroup, plans: Plan[]) {
  const apiMemberGroup = getApiMemberGroup(memberGroup, plans);
  return updateMemberGroup(memberGroup.id, apiMemberGroup);
}
